import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { ClientConfig } from '../types';
import { api, onSessionExpired } from '../services/api';

interface ConfigState {
  config: ClientConfig | null;
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null; // Timestamp when config was last fetched

  // Actions
  loadConfig: (force?: boolean) => Promise<void>;
  clearConfig: () => void;
}

const FOUR_HOURS_MS = 4 * 60 * 60 * 1000; // 4 hours in milliseconds

/**
 * Bump this whenever the persisted shape changes. Older persisted state is
 * discarded by `migrate` so a stale cache can never mask a schema change.
 */
const PERSIST_VERSION = 2;

/** Secrets that must never be written to localStorage. */
function stripSecrets(config: ClientConfig | null): ClientConfig | null {
  if (!config) return null;
  const { apiKey, previousApiKey, previousApiKeyExpiresAt, ...rest } = config as any;
  return rest as ClientConfig;
}

export const useConfigStore = create<ConfigState>()(
  persist(
    (set, get) => ({
      config: null,
      isLoading: false,
      error: null,
      lastFetched: null,

      loadConfig: async (force = false) => {
        const state = get();

        // Don't reload if already loading
        if (state.isLoading) return;

        // Check if we have cached config and it's not expired (unless force refresh)
        if (!force && state.config && state.lastFetched) {
          const age = Date.now() - state.lastFetched;
          if (age < FOUR_HOURS_MS) {
            return;
          }
        }

        set({ isLoading: true, error: null });
        try {
          const config = await api.getClientConfig();
          set({
            config: stripSecrets(config),
            isLoading: false,
            lastFetched: Date.now(),
          });
        } catch (err: any) {
          console.error('Error loading client config:', err);
          set({
            error: err.response?.data?.message || 'Failed to load configuration',
            isLoading: false,
          });
        }
      },

      clearConfig: () => {
        set({ config: null, error: null, isLoading: false, lastFetched: null });
      },
    }),
    {
      name: 'logopulse-config', // localStorage key
      version: PERSIST_VERSION,
      storage: createJSONStorage(() => localStorage),
      // Persist config and lastFetched timestamp, not loading/error states.
      // API keys are stripped so they never sit at rest in the browser.
      partialize: (state) => ({
        config: stripSecrets(state.config),
        lastFetched: state.lastFetched,
      }),
      migrate: (persisted: any, version: number) => {
        if (version !== PERSIST_VERSION) {
          // Drop any older shape entirely; it will be refetched.
          return { config: null, lastFetched: null };
        }
        return persisted;
      },
    }
  )
);

// When the API layer terminates the session (401 after refresh), drop the cache.
onSessionExpired(() => {
  useConfigStore.getState().clearConfig();
});
